import React from 'react';
import Card from '../common/Card';
import Text from '../common/Text';
import { CheckCircleIcon, ClockIcon, LightBulbIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';

interface AssessmentResultsProps {
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  timePerQuestion: number[];
  recommendations: string[];
  questions: Array<{
    id: number;
    payload: {
      question: string;
      options: string[];
      correct_answer: string;
      explanation?: string;
    };
    microtopic?: {
      id: number;
      name: string;
      subtopic?: {
        id: number;
        name: string;
        topic?: {
          id: number;
          name: string;
        };
      };
    };
  }>;
  answers: Record<string, string>;
}

const AssessmentResults: React.FC<AssessmentResultsProps> = ({
  score,
  correctAnswers,
  totalQuestions,
  timePerQuestion,
  recommendations,
  questions,
  answers
}) => {
  const formattedScore = Math.round(score * 100) / 100;
  const validTimePerQuestion = timePerQuestion.filter(time => !isNaN(time) && time !== null);
  const averageTimePerQuestion = validTimePerQuestion.length > 0
    ? Math.round(validTimePerQuestion.reduce((a, b) => a + b, 0) / validTimePerQuestion.length)
    : 0;

  // Calculate performance by microtopic
  const microtopicPerformance = React.useMemo(() => {
    const performanceMap = new Map<string, { correct: number; total: number }>();

    questions.forEach((question) => {
      const userAnswer = answers[question.id.toString()];
      const isCorrect = userAnswer === question.payload.correct_answer;
      const microtopicName = question.microtopic?.name || 'General Knowledge';

      const current = performanceMap.get(microtopicName) || { correct: 0, total: 0 };
      performanceMap.set(microtopicName, {
        correct: current.correct + (isCorrect ? 1 : 0),
        total: current.total + 1
      });
    });

    // Convert to array and ensure we have at least one data point
    const performanceData = Array.from(performanceMap.entries()).map(([name, stats]) => ({
      name,
      score: Math.round((stats.correct / stats.total) * 100),
      fullMark: 100
    }));

    // If no data points, add a default one
    if (performanceData.length === 0) {
      performanceData.push({
        name: 'General Knowledge',
        score: 0,
        fullMark: 100
      });
    }

    return performanceData;
  }, [questions, answers]);

  const getPerformanceLevel = (score: number) => {
    if (score >= 90) return 'Expert';
    if (score >= 75) return 'Advanced';
    if (score >= 60) return 'Intermediate';
    if (score >= 40) return 'Basic';
    return 'Beginner';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <Text variant="h1" className="text-3xl font-bold">
          Your Assessment Results
        </Text>
        <Text variant="p" className="mt-2 text-gray-600 dark:text-gray-300">
          Here's how you performed in your assessment.
        </Text>
      </div>

      {/* Score Overview */}
      <Card className="overflow-hidden">
        <div className="p-8 space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900/20">
                <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <Text variant="p" className="text-sm text-gray-600 dark:text-gray-400">
                  Score
                </Text>
                <Text variant="h3" className="text-2xl font-semibold">
                  {formattedScore}%
                </Text>
                <Text variant="p" className="text-sm text-gray-600 dark:text-gray-400">
                  {getPerformanceLevel(formattedScore)}
                </Text>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg dark:bg-blue-900/20">
                <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <Text variant="p" className="text-sm text-gray-600 dark:text-gray-400">
                  Avg. Time per Question
                </Text>
                <Text variant="h3" className="text-2xl font-semibold">
                  {averageTimePerQuestion}s
                </Text>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg dark:bg-purple-900/20">
                <LightBulbIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <Text variant="p" className="text-sm text-gray-600 dark:text-gray-400">
                  Correct Answers
                </Text>
                <Text variant="h3" className="text-2xl font-semibold">
                  {correctAnswers}/{totalQuestions}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Performance Breakdown */}
      <Card className="overflow-hidden">
        <div className="p-8 space-y-6">
          <Text variant="h3">Performance Breakdown by Topic</Text>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={microtopicPerformance}>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="name" tick={{ fill: '#6b7280' }} />
                <Radar
                  name="Performance"
                  dataKey="score"
                  stroke="#3b82f6"
                  fill="url(#colorGradient)"
                  fillOpacity={0.6}
                />
                <Radar
                  name="Target"
                  dataKey="fullMark"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.1}
                  dot={false}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {microtopicPerformance.map((topic) => (
              <div key={topic.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <Text variant="span" className="font-medium">{topic.name}</Text>
                <Text variant="span" className={`font-semibold ${
                  topic.score >= 80 ? 'text-green-600 dark:text-green-400' :
                  topic.score >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {Math.round(topic.score)}%
                </Text>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Recommendations */}
      <Card className="overflow-hidden">
        <div className="p-8 space-y-6">
          <Text variant="h3">Recommendations</Text>
          <div className="space-y-4">
            {recommendations.map((recommendation, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                </div>
                <Text variant="p">{recommendation}</Text>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Questions and Answers */}
      <Card className="overflow-hidden">
        <div className="p-8 space-y-6">
          <Text variant="h3">Questions and Answers</Text>
          <div className="space-y-8">
            {questions.map((question, index) => {
              const userAnswer = answers[question.id.toString()];
              const isCorrect = userAnswer === question.payload.correct_answer;

              return (
                <div key={question.id} className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 p-1 rounded-full ${isCorrect ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}>
                      {isCorrect ? (
                        <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <XMarkIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <Text variant="p" className="font-medium">
                        Question {index + 1}: {question.payload.question}
                      </Text>
                      <div className="mt-4 space-y-2">
                        {question.payload.options.map((option, optionIndex) => {
                          const isUserAnswer = option === userAnswer;
                          const isCorrectAnswer = option === question.payload.correct_answer;

                          return (
                            <div
                              key={optionIndex}
                              className={`p-3 rounded-lg border ${
                                isUserAnswer && !isCorrect
                                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                  : isCorrectAnswer
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                  isUserAnswer && !isCorrect
                                    ? 'bg-red-500'
                                    : isCorrectAnswer
                                    ? 'bg-green-500'
                                    : 'bg-gray-300 dark:bg-gray-600'
                                }`}>
                                  {isUserAnswer && !isCorrect ? (
                                    <XMarkIcon className="w-3 h-3 text-white" />
                                  ) : isCorrectAnswer ? (
                                    <CheckIcon className="w-3 h-3 text-white" />
                                  ) : null}
                                </div>
                                <Text variant="span" className={`${
                                  isUserAnswer && !isCorrect
                                    ? 'text-red-600 dark:text-red-400'
                                    : isCorrectAnswer
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-gray-600 dark:text-gray-300'
                                }`}>
                                  {option}
                                </Text>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {question.payload.explanation && (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <LightBulbIcon className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                              <Text variant="span" className="font-medium text-blue-700 dark:text-blue-300">Explanation:</Text>
                              <Text variant="p" className="mt-1 text-blue-600 dark:text-blue-400">
                                {question.payload.explanation}
                              </Text>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AssessmentResults;